import { useCallback, useRef, useState } from 'react'
import { Formik } from 'formik'
import { useLocation, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { compact, isEmpty, isEqual } from 'lodash'
import * as Yup from 'yup'

import Info from 'components/RevenueCenters/Form/Info'
import BusinessEndTime from 'components/RevenueCenters/Form/BusinessEndTime'
import RevenueCenterImage from 'components/RevenueCenters/Form/RevenueCenterImage'
import Merchant from 'components/RevenueCenters/Form/Merchant'
import FilterData from 'components/RevenueCenters/Form/FilterData'
import Menus from 'components/RevenueCenters/Form/Menus'
import RevenueCenter from 'components/RevenueCenters/Form/Drawers/RevenueCenter'
import Items from 'components/RevenueCenters/Form/Drawers/Items'
import Menu from 'components/RevenueCenters/Form/Drawers/Menu'
import PaymentTenderSettings from 'components/RevenueCenters/Form/PaymentTenderSettings'
import TipConfigurationsSettings from 'components/RevenueCenters/Form/TipConfigurationSettings'

import {
  getAssignedItemsToMenus,
  getCurrentRevenueCenter,
  getFilteredDataForRevenueCenter,
  getMenusForRevenueCenter,
} from 'store/RevenueCenters/selector'
import { getItems } from 'store/Items/selector'
import {
  createRevenueCenter,
  createSettings,
  updateRevenueCenter,
  setRevenueCenterOptimisticListImage,
} from 'store/RevenueCenters/actions'
import { createMenus, updateMenus } from 'store/Menus/actions'
import { updateGlobalItem } from 'store/Items/actions'
import { getOrgName } from 'store/AppState/selector'
import { getCurrentVenueUuid } from 'store/Venues/selector'
import {
  getPreparedMenusData,
  getPreparedRevenueCenterData,
  getPreparedItemsData,
  getPreparedSettingsData,
} from 'utils/revenueCenters/apiDataPreparation'
import { getChangedValues } from 'utils/formik'
import { checkMenuPrefix } from 'utils/revenueCenters'
import {
  getDefaultSettingsForStands,
  getSettingsForStands,
} from 'store/Settings/selectors'

import { hasAny } from 'utils/hasAny'
import api from 'utils/api'
import { createRevenueCenterImageFormData } from 'utils/images'
import {
  createRevenueCenterImageLink,
  createMenuImageLink,
} from 'utils/links'
import useFetchImages from 'hooks/useFetchImages'
import useUploadMultipleImagesToS3 from 'hooks/useUploadMultipleImagesToS3'
import useUploadImageToS3 from 'hooks/useUploadImageToS3'
import settingsUtils from 'utils/settings'
import { revenueCentersUtils } from 'components/RevenueCenters/utils'
import { validateTipOptions } from 'components/RevenueCenters/utils/validation'
import { SETTINGS_LEVELS } from 'resources'

const RevenueCentersForm = ({ isLoading }) => {
  const [showValidationErrors, setShowValidationErrors] =
    useState(false)

  const { state: locationState } = useLocation()

  const history = useHistory()
  const dispatch = useDispatch()

  const modalType = locationState?.modalType
  const revenueCenterUuid = locationState?.revenueCenterUuid

  const venueUuid = useSelector(getCurrentVenueUuid)
  const orgName = useSelector(getOrgName)

  const allItems = useSelector(getItems)
  const currentRevenueCenter = useSelector((state) =>
    getCurrentRevenueCenter(state, revenueCenterUuid)
  )

  const filterData = useSelector((state) =>
    getFilteredDataForRevenueCenter(state, revenueCenterUuid)
  )

  const menusForCurrentRevenueCenter = useSelector((state) =>
    getMenusForRevenueCenter(state, currentRevenueCenter.id)
  )

  const menusForCuttentRevenueCenterUuids =
    menusForCurrentRevenueCenter.map(({ uuid }) => uuid)

  const assignedItemsToMenus = useSelector((state) =>
    getAssignedItemsToMenus(state, menusForCuttentRevenueCenterUuids)
  )

  const isNewRevenueCenter = revenueCenterUuid === 'new'

  const {
    uploadImageToS3,
    isLoading: isUploadingRevenueCenterImageLoading,
  } = useUploadImageToS3()

  const {
    uploadMultipleImagesToS3,
    isLoading: isUploadingMenuImageLoading,
  } = useUploadMultipleImagesToS3()

  const {
    images: revenueCenterImages,
    loading: isRevenueCenterImageLoading,
  } = useFetchImages(
    !isNewRevenueCenter
      ? createRevenueCenterImageLink(
          [currentRevenueCenter.uuid],
          orgName
        )
      : null
  )

  const menusForCurrentRevenueCenterUuids =
    menusForCurrentRevenueCenter.map(({ uuid }) => uuid)

  const { images: menuImages, loading: isMenuImageLoading } =
    useFetchImages(
      !isNewRevenueCenter
        ? createMenuImageLink(
            menusForCurrentRevenueCenterUuids,
            orgName
          )
        : null
    )

  const revenueCenterImage =
    revenueCenterImages?.[currentRevenueCenter.uuid] ?? null

  // settings
  const standsSettings = useSelector(getSettingsForStands)

  const defaultSettings = useSelector(getDefaultSettingsForStands)

  const transformedStandSettings =
    revenueCentersUtils.getTransformedStandsSettings(
      standsSettings,
      defaultSettings,
      currentRevenueCenter,
      venueUuid,
      orgName
    )

  const isVenueLevelSettingExist =
    settingsUtils.checkDesireSettingsLevel({
      settings: standsSettings,
      level: SETTINGS_LEVELS.venueUuid,
      venueUuid,
      organizationName: orgName,
    })

  const initialValues = {
    revenueCenterImage: revenueCenterImage,
    menuImages: menuImages,
    name: currentRevenueCenter?.name,
    externalRefId: currentRevenueCenter?.externalRefId,
    description: currentRevenueCenter?.description,
    localTime: currentRevenueCenter?.localTime,
    businessEndTime: currentRevenueCenter?.businessEndTime ?? null,
    filterData,
    merchantId: currentRevenueCenter?.merchantId,
    menus: menusForCurrentRevenueCenter,
    assignedItemsToMenus: assignedItemsToMenus,
    standsSettings: transformedStandSettings,
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required').min(3, 'Too Short!'),
    merchantId: Yup.string()
      .nullable()
      .test({
        name: 'merchantId',
        message: 'Required',
        test: (value, context) => {
          const menusAssignedToRevenueCenter = context?.parent?.menus

          const menusServiceTypes = menusAssignedToRevenueCenter?.map(
            ({ serviceType }) => serviceType
          )

          const hasParticularServiceTypes = hasAny(
            menusServiceTypes,
            ['Concessions', 'Delivery', 'SelfCheckout', 'QuickPay']
          )

          if (hasParticularServiceTypes) {
            return !isEmpty(value)
          }

          return true
        },
      }),
    menus: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('Required').typeError('Required'),
        configurationModelId: Yup.string()
          .test({
            name: 'configurationModelId',
            message: 'Required',
            test: (value, context) => {
              const serviceType = context?.parent?.serviceType

              if (isEmpty(serviceType)) {
                return !isEmpty(value)
              }

              return true
            },
          })
          .nullable(),
        merchantId: Yup.string()
          .nullable()
          .test({
            name: 'merchantId',
            message: 'Required',
            test: (value, context) => {
              const menuModelName =
                context?.parent?.configurationModelName

              const isKioskOrPos = checkMenuPrefix(menuModelName, [
                'kiosk',
                'pos',
              ])

              if (isKioskOrPos) {
                return !isEmpty(value)
              }
              return true
            },
          }),
      })
    ),
    standsSettings: Yup.object().shape({
      tip_values: Yup.array()
        .of(Yup.string().nullable())
        .test({
          name: 'tip_values',
          message: 'Tip values must be unique',
          test: (value, context) =>
            validateTipOptions(
              value,
              context?.parent?.provide_tip_suggestions
            ),
        }),
      selected_tip_option: Yup.number()
        .nullable()
        .when(
          ['provide_tip_suggestions', 'tip_values'],
          (provide_tip_suggestions, tip_values, schema) => {
            const filteredTipValues = compact(tip_values)

            if (
              !provide_tip_suggestions ||
              filteredTipValues.length === 0
            ) {
              return schema
            }

            return schema.required('Required')
          }
        ),
    }),
  })

  const sectionRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ]

  const settings = [
    {
      Component: Info,
      ref: sectionRefs[0],
      text: 'info',
      url: '#info',
      revenueCenterUuid: currentRevenueCenter?.uuid,
    },
    {
      Component: RevenueCenterImage,
      ref: sectionRefs[1],
      isRevenueCenterImageLoading,
      text: 'Revenue Center Image',
      url: '#revenueCenterImage',
    },
    {
      Component: BusinessEndTime,
      ref: sectionRefs[2],
      text: 'Business End Time',
      url: '#businessEndTime',
    },
    {
      Component: Merchant,
      ref: sectionRefs[3],
      text: 'Revenue Center Merchant',
      url: '#merchant',
      showValidationErrors,
    },
    {
      Component: Menus,
      ref: sectionRefs[4],
      text: 'Menus',
      url: '#menus',
      showValidationErrors,
      setShowValidationErrors,
    },
    {
      Component: FilterData,
      ref: sectionRefs[5],
      text: 'Filter Data',
      url: '#filterData',
    },
    {
      Component: PaymentTenderSettings,
      ref: sectionRefs[6],
      text: 'Payment & Tender Settings',
    },
    {
      Component: TipConfigurationsSettings,
      ref: sectionRefs[7],
      text: 'Tip Configurations',
      isVenueLevelSettingExist,
    },
  ]

  const onClickSaveButton = () => {
    setShowValidationErrors(true)
  }

  const disableUpdateButton = useCallback(
    (dirty, errors, isSubmitting) => {
      const disableButton =
        !dirty ||
        (showValidationErrors && !isEmpty(errors)) ||
        isSubmitting

      return disableButton
    },
    [showValidationErrors]
  )

  const onFormKeyDown = (event) => {
    if ((event.charCode || event.keyCode) === 13) {
      event.preventDefault()
    }
  }

  const onCloseModal = () => {
    history.push({ state: { revenueCenterUuid: null } })
  }

  const onSubmit = (values) => {
    // revenue centers data
    const { revenueCenterDataToUpdate, revenueCenterToCreate } =
      getPreparedRevenueCenterData(
        values,
        initialValues,
        revenueCenterUuid,
        orgName,
        venueUuid
      )

    // menus data
    const { menusDataToUpdate, menusDataToCreate } =
      getPreparedMenusData(values, initialValues)

    // items data
    const updatedItems = getPreparedItemsData(
      initialValues.assignedItemsToMenus,
      values.assignedItemsToMenus,
      allItems
    )

    // settings data
    const updatedSettings = getPreparedSettingsData(
      values?.standsSettings,
      initialValues?.standsSettings,
      venueUuid,
      currentRevenueCenter?.id
    )

    const revenueCenterImageFormData =
      createRevenueCenterImageFormData(
        values.revenueCenterImage,
        currentRevenueCenter?.uuid,
        orgName
      )

    const menuImageDataToUpload = getChangedValues(
      values.menuImages,
      initialValues.menuImages
    )

    const onMenuImageUpload = !isEmpty(menuImageDataToUpload)
      ? uploadMultipleImagesToS3(menuImageDataToUpload, orgName)
      : Promise.resolve()

    const onRevenueCenterImageUpload =
      !isEqual(
        values.revenueCenterImage,
        initialValues.revenueCenterImage
      ) && !isNewRevenueCenter
        ? uploadImageToS3(revenueCenterImageFormData)
            .then(() =>
              dispatch(
                setRevenueCenterOptimisticListImage({
                  uuid: revenueCenterUuid,
                  image: values.revenueCenterImage,
                })
              )
            )
            .then(() =>
              history.push({
                state: {
                  ...history.location.state,
                  updatedResourceUuid: currentRevenueCenter?.uuid,
                },
              })
            )
        : Promise.resolve()

    if (isNewRevenueCenter) {
      dispatch(createRevenueCenter(revenueCenterToCreate))
        .then((response) => {
          const newMenus = menusDataToCreate?.map((menu) => {
            if (isEmpty(menu.standId)) {
              return {
                ...menu,
                standId: response.payload?.[0]?.id,
              }
            } else {
              return menu
            }
          })

          dispatch(createMenus(newMenus)).then(() => {
            if (!isEmpty(updatedItems)) {
              dispatch(updateGlobalItem(updatedItems))
            }
          })

          if (!isEmpty(updatedSettings)) {
            const newSettings = getPreparedSettingsData(
              values?.standsSettings,
              initialValues?.standsSettings,
              venueUuid,
              response.payload?.[0]?.id
            )

            dispatch(createSettings(newSettings))
          }

          const newRevenueCenterImageFormData =
            createRevenueCenterImageFormData(
              values.revenueCenterImage,
              response.payload?.[0]?.uuid,
              orgName
            )

          if (values.revenueCenterImage) {
            api
              .uploadImageToS3(newRevenueCenterImageFormData)
              .then(() =>
                history.push({
                  state: {
                    ...history.location.state,
                    updatedResourceUuid: response.payload?.[0]?.uuid,
                  },
                })
              )
          }
        })
        .then(() => {
          history.push({ state: { revenueCenterUuid: null } })
        })
    } else {
      const revenueCenterToUpdate = dispatch(
        updateRevenueCenter(revenueCenterDataToUpdate)
      )
      const menusToUpdate = !isEmpty(menusDataToUpdate)
        ? dispatch(updateMenus(menusDataToUpdate))
        : Promise.resolve()
      const menusToCreate = !isEmpty(menusDataToCreate)
        ? dispatch(createMenus(menusDataToCreate))
        : Promise.resolve()
      const settingsToUpdate = !isEmpty(updatedSettings)
        ? dispatch(createSettings(updatedSettings))
        : Promise.resolve()

      Promise.all([
        revenueCenterToUpdate,
        menusToUpdate,
        menusToCreate,
        settingsToUpdate,
        onRevenueCenterImageUpload,
        onMenuImageUpload,
      ]).then(() => {
        // we have to update items in then block because we need to wait for menus to be created
        if (!isEmpty(updatedItems)) {
          dispatch(updateGlobalItem(updatedItems))
        }
      })

      history.push({ state: { revenueCenterUuid: null } })
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnMount
      enableReinitialize
    >
      {() => (
        <>
          {isEmpty(modalType) && (
            <RevenueCenter
              settings={settings}
              showValidationErrors={showValidationErrors}
              onClickSaveButton={onClickSaveButton}
              disableUpdateButton={disableUpdateButton}
              revenueCenterUuid={revenueCenterUuid}
              onCloseModal={onCloseModal}
              isLoading={
                isLoading ||
                isUploadingMenuImageLoading ||
                isUploadingRevenueCenterImageLoading
              }
              onFormKeyDown={onFormKeyDown}
            />
          )}

          {modalType === 'items' && <Items />}
          {modalType === 'menu' && (
            <Menu isMenuImageLoading={isMenuImageLoading} />
          )}
        </>
      )}
    </Formik>
  )
}

export default RevenueCentersForm
